<template>
  <BaseTemplate app-name="Medical System" page-title="">
    <template #content>
      <div class="h-screen flex flex-col bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-100 transition-colors duration-200">
        
        <!-- Main Content -->
        <div class="flex-1 flex overflow-hidden">
          
          <!-- Content Area -->
          <div class="flex-1 overflow-hidden flex flex-col">
            <!-- Header -->
            <div class="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4 shadow-sm">
              <div class="flex flex-wrap justify-between items-center gap-4">
                <div class="flex items-center gap-3">
                  <h2 class="text-xl font-medium">Edição de Exames PACS</h2>
                </div>
                <div class="flex items-center gap-2">
                  <button
                    @click="refreshData"
                    class="px-4 py-2 clinic-btn text-white rounded-md transition-colors shadow-sm flex items-center gap-2"
                  >
                    <RefreshCwIcon class="w-4 h-4" />
                    <span>Atualizar</span>
                  </button>
                </div>
              </div>
            </div>



            <!-- Main Content -->
            <div class="flex-1 overflow-y-auto p-4 bg-gray-50 dark:bg-gray-900">
              <div class="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-300 dark:border-gray-700 p-4">
                
                <!-- Search and Filters -->
                <div class="mb-4 flex flex-wrap gap-4">
                  <div class="flex-1 min-w-[240px]">
                    <div class="relative">
                      <input
                        v-model="searchTerm"
                        @input="filterStudies"
                        type="text"
                        placeholder="Buscar por accession, paciente ou descrição..."
                        class="w-full px-3 py-2 pl-10 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      />
                      <SearchIcon class="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                    </div>
                  </div>
                  <div class="w-full md:w-auto flex gap-2">
                    <div class="relative">
                      <select
                        v-model="selectedModality"
                        @change="filterStudies"
                        class="h-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent appearance-none pr-8"
                      >
                        <option value="">Todas as modalidades</option>
                        <option v-for="modality in modalities" :key="modality" :value="modality">
                          {{ modality }}
                        </option>
                      </select>
                      <div class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                        <ChevronDownIcon class="w-4 h-4 text-gray-500" />
                      </div>
                    </div>
                    <input
                      v-model="dateFilter"
                      type="date"
                      @change="filterStudies"
                      class="px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                    <button
                      @click="resetFilters"
                      class="px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                      <RefreshCwIcon class="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <!-- Studies Table -->
                <div class="overflow-x-auto">
                  <table class="w-full border-collapse">
                    <thead>
                      <tr class="bg-gray-50 dark:bg-gray-900/50 text-left">
                        <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                          Accession
                        </th>
                        <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                          Paciente
                        </th>
                        <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                          Modalidade
                        </th>
                        <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                          Data
                        </th>
                        <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                          Séries
                        </th>
                        <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                          Imagens
                        </th>
                        <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                          Status
                        </th>
                        <th class="px-4 py-3 text-sm font-medium text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="loading" class="text-center">
                        <td colspan="8" class="px-4 py-8">
                          <div class="flex items-center justify-center">
                            <RefreshCwIcon class="w-6 h-6 animate-spin text-emerald-600 dark:text-emerald-400 mr-2" />
                            <span class="text-gray-600 dark:text-gray-400">Carregando exames...</span>
                          </div>
                        </td>
                      </tr>
                      <tr v-else-if="studies.length === 0" class="text-center">
                        <td colspan="8" class="px-4 py-8">
                          <span class="text-gray-500 dark:text-gray-400">Nenhum exame encontrado</span>
                        </td>
                      </tr>
                      <tr v-for="study in studies" :key="study.accession_number"
                        class="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                        :class="{ 'bg-yellow-50 dark:bg-yellow-900/20': study.status && study.status.includes('Junção') }"
                      >
                        <td class="px-4 py-3 text-sm font-medium">
                          <div class="flex items-center gap-2">
                            {{ study.accession_number }}
                            <span v-if="study.status && study.status.includes('Junção')" 
                                  class="px-2 py-1 text-yellow-800 dark:text-yellow-200 rounded-full text-xs"
                                  title="Este exame foi juntado com outro">
                              
                            </span>
                          </div>
                        </td>
                        <td class="px-4 py-3">
                          <div class="flex items-center gap-3">
                            <div
                              class="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium flex-shrink-0"
                              :style="{ backgroundColor: getStudyColor(study.accession_number) }"
                            >
                              {{ getInitials(study.patient_name) }}
                            </div>
                            <div class="text-sm">{{ study.patient_name }}</div>
                          </div>
                        </td>
                        <td class="px-4 py-3 text-sm">
                          <span class="px-2 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200 rounded-full text-xs">
                            {{ study.modality }}
                          </span>
                        </td>
                        <td class="px-4 py-3 text-sm">{{ formatDate(study.study_date) }}</td>
                        <td class="px-4 py-3 text-sm">{{ study.series_count }}</td>
                        <td class="px-4 py-3 text-sm">{{ study.images_count }}</td>
                        <td class="px-4 py-3 text-sm">
                          <span v-if="study.status" 
                                class="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded-full text-xs"
                                :class="{ 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200': study.status.includes('Junção') }">
                            {{ study.status }}
                          </span>
                          <span v-else class="text-gray-400 text-xs">-</span>
                        </td>
                        <td class="px-4 py-3 text-sm">
                          <div class="flex gap-2">
                            <button
                              @click="editStudy(study)"
                              class="p-1 text-gray-500 hover:text-emerald-600 dark:text-gray-400 dark:hover:text-emerald-400"
                              title="Editar"
                              :disabled="study.status && study.status.includes('Junção')"
                            >
                              <EditIcon class="w-4 h-4" />
                            </button>
                            <button
                              @click="deleteStudy(study)"
                              class="p-1 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                              title="Excluir exame completo"
                              :disabled="study.status && study.status.includes('Junção')"
                            >
                              <TrashIcon class="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  
                  <!-- Paginação -->
                  <div v-if="totalPages > 1" class="mt-6 flex items-center justify-between">
                    <div class="flex items-center gap-4">
                      <span class="text-sm text-gray-600 dark:text-gray-400">
                        Mostrando {{ ((currentPage - 1) * pageSize) + 1 }} a {{ Math.min(currentPage * pageSize, totalCount) }} de {{ totalCount }} exames
                      </span>
                      <div class="flex items-center gap-2">
                        <span class="text-sm text-gray-600 dark:text-gray-400">Itens por página:</span>
                        <select 
                          v-model="pageSize" 
                          @change="changePageSize(pageSize)"
                          class="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800"
                        >
                          <option value="10">10</option>
                          <option value="20">20</option>
                          <option value="50">50</option>
                          <option value="100">100</option>
                        </select>
                      </div>
                    </div>
                    
                    <div class="flex items-center gap-2">
                      <button
                        @click="goToPage(currentPage - 1)"
                        :disabled="currentPage <= 1"
                        class="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
                        </svg>
                        Anterior
                      </button>
                      
                      <div class="flex items-center gap-1">
                        <button
                          v-for="page in getVisiblePages()"
                          :key="page"
                          @click="goToPage(page)"
                          :class="[
                            'px-3 py-1 text-sm border rounded',
                            page === currentPage
                              ? 'bg-emerald-500 text-white border-emerald-500'
                              : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                          ]"
                        >
                          {{ page }}
                        </button>
                      </div>
                      
                      <button
                        @click="goToPage(currentPage + 1)"
                        :disabled="currentPage >= totalPages"
                        class="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        Próximo
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Edit Study Modal -->
        <div v-if="showEditModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
            <div class="p-6">
              <div class="flex justify-between items-center mb-6">
                <h3 class="text-xl font-medium">Editar Exame: {{ selectedStudy?.accession_number }}</h3>
                <button @click="closeEditModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                  <XIcon class="w-6 h-6" />
                </button>
              </div>
              
              <!-- Tabs -->
              <div class="border-b border-gray-200 dark:border-gray-700 mb-6">
                <nav class="flex space-x-8">
                  <button
                    @click="activeTab = 'edit'"
                    :class="[
                      'py-2 px-1 border-b-2 font-medium text-sm transition-colors',
                      activeTab === 'edit'
                        ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                    ]"
                  >
                    Editar Exame
                  </button>
                  <button
                    @click="activeTab = 'details'"
                    :class="[
                      'py-2 px-1 border-b-2 font-medium text-sm transition-colors',
                      activeTab === 'details'
                        ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                    ]"
                  >
                    Detalhes e Imagens
                  </button>
                </nav>
              </div>
              
              <!-- Tab Content -->
              <div v-if="activeTab === 'edit'" class="space-y-6">
                <!-- Edit Accession and Merge Exam - Side by Side -->
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <!-- Edit Accession -->
                  <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                    <div class="flex items-center gap-2 mb-4">
                      <HashIcon class="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                      <h4 class="text-lg font-medium">Editar Accession</h4>
                    </div>
                    <div class="space-y-4">
                      <div>
                        <label class="block text-sm font-medium mb-1 flex items-center gap-2">
                          <FileTextIcon class="w-4 h-4" />
                          Novo Accession:
                        </label>
                        <input
                          v-model="newAccession"
                          type="text"
                          placeholder="Digite o novo accession"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label class="block text-sm font-medium mb-1 flex items-center gap-2">
                          <InfoIcon class="w-4 h-4" />
                          Motivo:
                        </label>
                        <textarea
                          v-model="editReason"
                          placeholder="Descreva o motivo da alteração"
                          rows="3"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        ></textarea>
                      </div>
                      <button @click="updateAccession" class="px-4 py-2 clinic-btn text-white rounded-md transition-colors shadow-sm flex items-center gap-2">
                        <SaveIcon class="w-4 h-4" />
                        Atualizar Accession
                      </button>
                    </div>
                  </div>

                  <!-- Merge Exam -->
                  <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                    <div class="flex items-center gap-2 mb-4">
                      <MergeIcon class="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                      <h4 class="text-lg font-medium">Juntar com Outro Exame</h4>
                    </div>
                    <div class="space-y-4">
                      <div>
                        <label class="block text-sm font-medium mb-1 flex items-center gap-2">
                          <HashIcon class="w-4 h-4" />
                          Accession de Destino:
                        </label>
                        <input
                          v-model="mergeTargetAccession"
                          type="text"
                          placeholder="Digite o accession de destino"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label class="block text-sm font-medium mb-1 flex items-center gap-2">
                          <InfoIcon class="w-4 h-4" />
                          Motivo:
                        </label>
                        <textarea
                          v-model="mergeReason"
                          placeholder="Descreva o motivo da junção"
                          rows="3"
                          class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        ></textarea>
                      </div>
                      <button @click="mergeExam" class="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-md transition-colors shadow-sm flex items-center gap-2">
                        <MergeIcon class="w-4 h-4" />
                        Juntar Exames
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Details Tab -->
              <div v-if="activeTab === 'details' && studyDetails" class="space-y-6">
                <!-- Study Information -->
                <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h4 class="text-lg font-medium mb-4">Informações do Exame</h4>
                  <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label class="block text-sm font-medium text-gray-600 dark:text-gray-400">Accession:</label>
                      <span class="text-sm">{{ studyDetails.accession_number }}</span>
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-600 dark:text-gray-400">Modalidade:</label>
                      <span class="text-sm">{{ studyDetails.modality }}</span>
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-600 dark:text-gray-400">Data:</label>
                      <span class="text-sm">{{ formatDate(studyDetails.study_date) }}</span>
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-600 dark:text-gray-400">Paciente:</label>
                      <span class="text-sm">{{ studyDetails.patient_name }}</span>
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-600 dark:text-gray-400">Descrição:</label>
                      <span class="text-sm">{{ studyDetails.study_description || 'N/A' }}</span>
                    </div>
                  </div>
                </div>

                <!-- Series -->
                <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h4 class="text-lg font-medium mb-4">Séries ({{ studyDetails.series.length }})</h4>
                  <div class="space-y-3">
                    <div v-for="series in studyDetails.series" :key="series.series_instance_uid" class="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                      <div class="flex justify-between items-center">
                        <div>
                          <h5 class="font-medium">{{ series.series_description || 'Série sem descrição' }}</h5>
                          <div class="flex gap-2 mt-1">
                            <span class="px-2 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200 rounded-full text-xs">
                              {{ series.modality }}
                            </span>
                            <span class="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded-full text-xs">
                              {{ series.images_count }} imagens
                            </span>
                          </div>
                        </div>
                        <button @click="deleteSeries(series)" class="px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-sm transition-colors">
                          Excluir Série
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Images Summary -->
                <div class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div class="flex justify-between items-center mb-4">
                    <h4 class="text-lg font-medium">Resumo de Imagens ({{ studyDetails.images.length }})</h4>
                    <button 
                      @click="showAllImages = !showAllImages" 
                      class="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-sm transition-colors flex items-center gap-2"
                    >
                      {{ showAllImages ? 'Ocultar Detalhes' : 'Ver Todas as Imagens' }}
                    </button>
                  </div>
                  
                  <!-- Images Summary by Series -->
                  <div class="space-y-4">
                    <div v-for="series in studyDetails.series" :key="series.series_instance_uid" class="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                      <div class="flex justify-between items-start">
                        <div class="flex-1">
                          <h5 class="font-medium text-lg mb-2">{{ series.series_description || 'Série sem descrição' }}</h5>
                          <div class="flex gap-2 mb-3">
                            <span class="px-2 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200 rounded-full text-xs">
                              {{ series.modality }}
                            </span>
                            <span class="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded-full text-xs">
                              {{ series.images_count }} imagens
                            </span>
                          </div>
                          
                          <!-- Sample Images (first 5) -->
                          <div v-if="getSeriesImages(series).length > 0" class="space-y-2">
                            <p class="text-sm text-gray-600 dark:text-gray-400">
                              <strong>Primeiras {{ Math.min(5, getSeriesImages(series).length) }} imagens:</strong>
                            </p>
                            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                              <div v-for="image in getSeriesImages(series).slice(0, 5)" :key="image.sop_instance_uid" 
                                   class="bg-gray-50 dark:bg-gray-700 rounded p-2 text-xs">
                                <p class="font-medium truncate">{{ image.sop_instance_uid.substring(0, 20) }}...</p>
                                <p class="text-gray-500 truncate">{{ getFileName(image.file_path) }}</p>
                                <button @click="deleteImage(image)" class="mt-1 w-full px-2 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-xs transition-colors">
                                  Excluir
                                </button>
                              </div>
                            </div>
                            
                            <!-- Show more indicator -->
                            <div v-if="getSeriesImages(series).length > 5" class="text-center py-2">
                              <span class="text-sm text-gray-500 dark:text-gray-400">
                                + {{ getSeriesImages(series).length - 5 }} imagens adicionais
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <button @click="deleteSeries(series)" class="px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-sm transition-colors ml-4">
                          Excluir Série
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <!-- All Images (Collapsible) -->
                  <div v-if="showAllImages" class="mt-6">
                    <div class="border-t border-gray-200 dark:border-gray-600 pt-4">
                      <h5 class="text-md font-medium mb-3">Todas as Imagens ({{ studyDetails.images.length }})</h5>
                      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-96 overflow-y-auto">
                        <div v-for="image in studyDetails.images" :key="image.sop_instance_uid" 
                             class="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                          <div class="space-y-2">
                            <p class="text-sm"><strong>UID:</strong> {{ image.sop_instance_uid.substring(0, 30) }}...</p>
                            <p class="text-sm"><strong>Arquivo:</strong> {{ getFileName(image.file_path) }}</p>
                            <button @click="deleteImage(image)" class="w-full px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-sm transition-colors">
                              Excluir Imagem
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>



        <!-- Banner Message -->
        <BannerMessage
          v-if="banner.show"
          :type="banner.type"
          :message="banner.message"
          @close="banner.show = false"
        />
      </div>
    </template>
  </BaseTemplate>

  <!-- Modal de Confirmação de Exclusão -->
  <div v-if="showDeleteModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
      <div class="p-6">
        <div class="flex items-center gap-3 mb-4">
          <div class="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
            <TrashIcon class="w-6 h-6 text-red-600 dark:text-red-400" />
          </div>
          <div>
            <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100">Confirmar Exclusão</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400">Esta ação não pode ser desfeita</p>
          </div>
        </div>
        <div class="mb-6">
          <p class="text-gray-700 dark:text-gray-300 mb-3">
            Você está prestes a excluir <strong>completamente</strong> o exame:
          </p>
          <div class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-medium"
                   :style="{ backgroundColor: getStudyColor(studyToDelete?.accession_number) }">
                {{ getInitials(studyToDelete?.patient_name) }}
              </div>
              <div>
                <p class="font-medium text-red-800 dark:text-red-200">
                  {{ studyToDelete?.accession_number }}
                </p>
                <p class="text-sm text-red-600 dark:text-red-300">
                  {{ studyToDelete?.patient_name }}
                </p>
              </div>
            </div>
          </div>
          <div class="mt-2 space-y-2 text-sm text-gray-600 dark:text-gray-400">
            <div class="flex items-center gap-2">
              <TrashIcon class="w-4 h-4 text-red-500" />
              <span>{{ studyToDelete?.series_count }} séries serão excluídas</span>
            </div>
            <div class="flex items-center gap-2">
              <TrashIcon class="w-4 h-4 text-red-500" />
              <span>{{ studyToDelete?.images_count }} imagens serão excluídas</span>
            </div>
            <div class="flex items-center gap-2">
              <TrashIcon class="w-4 h-4 text-red-500" />
              <span>Todos os arquivos físicos serão removidos do Storage</span>
            </div>
          </div>
          <div class="mt-6">
            <label class="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">Digite o accession que está excluindo para confirmação:</label>
            <input
              v-model="deleteConfirmAccession"
              type="text"
              :placeholder="studyToDelete?.accession_number || ''"
              class="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              @keyup.enter="canDeleteExam && confirmDelete()"
              autocomplete="off"
            />
            <p v-if="deleteConfirmAccession && deleteConfirmAccession !== studyToDelete?.accession_number" class="text-xs text-red-500 mt-1">O accession digitado não confere.</p>
          </div>
        </div>
        <div class="flex gap-3">
          <button
            @click="cancelDelete"
            class="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancelar
          </button>
          <button
            @click="confirmDelete"
            :disabled="!canDeleteExam"
            class="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <TrashIcon class="w-4 h-4" />
            Excluir Exame
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Banner Messages -->
  <BannerMessage
    v-if="banner.show"
    :type="banner.type"
    :message="banner.message"
    @close="banner.show = false"
  />
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import BaseTemplate from '../templates/BaseTemplate.vue';
import BannerMessage from '@/components/BannerMessage.vue';
import apiClient from '@/services/authService';
import { API_ENDPOINTS } from '@/services/apiConfig';
import {
  Search, Edit, X, RefreshCw, ChevronDown,
  FileText, Merge, Save, 
  Hash,  Info, Trash2
} from 'lucide-vue-next';

// Icons
const SearchIcon = Search;
const EditIcon = Edit;
const XIcon = X;
const RefreshCwIcon = RefreshCw;
const ChevronDownIcon = ChevronDown;
const FileTextIcon = FileText;
const MergeIcon = Merge;

const SaveIcon = Save;
const HashIcon = Hash;
const InfoIcon = Info;
const TrashIcon = Trash2;


// Reactive data
const studies = ref([]);
const modalities = ref([]);
const searchTerm = ref('');
const selectedModality = ref('');
const dateFilter = ref('');
const showEditModal = ref(false);
const selectedStudy = ref(null);
const studyDetails = ref(null);
const newAccession = ref('');
const editReason = ref('');
const mergeTargetAccession = ref('');
const mergeReason = ref('');
const showAllImages = ref(false);
const activeTab = ref('edit');

const showDeleteModal = ref(false);
const studyToDelete = ref(null);
const deleteConfirmAccession = ref("");
const canDeleteExam = computed(() => {
  return (
    studyToDelete.value &&
    deleteConfirmAccession.value === studyToDelete.value.accession_number
  );
});

// Paginação
const currentPage = ref(1);
const pageSize = ref(20);
const totalPages = ref(1);
const totalCount = ref(0);
const loading = ref(false);

const banner = ref({ show: false, type: 'success', message: '' });

// Methods
async function fetchStudies() {
  try {
    loading.value = true;
    const params = {
      page: currentPage.value,
      page_size: pageSize.value
    };
    
    if (searchTerm.value) params.search = searchTerm.value;
    if (selectedModality.value) params.modality = selectedModality.value;
    if (dateFilter.value) params.date_filter = dateFilter.value;
    
    const res = await apiClient.get(API_ENDPOINTS.EDIT_EXAM.STUDIES, { params });
    studies.value = res.data.studies;
    
    // Atualizar informações de paginação
    if (res.data.pagination) {
      currentPage.value = res.data.pagination.current_page;
      totalPages.value = res.data.pagination.total_pages;
      totalCount.value = res.data.pagination.total_count;
    }
  } catch (error) {
    showBanner('error', 'Erro ao carregar exames');
  } finally {
    loading.value = false;
  }
}

async function fetchModalities() {
  try {
    const res = await apiClient.get(API_ENDPOINTS.EDIT_EXAM.MODALITIES);
    modalities.value = res.data.modalities;
  } catch (error) {
    showBanner('error', 'Erro ao carregar modalidades');
  }
}

function goToPage(page) {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
    fetchStudies();
  }
}

function changePageSize(newSize) {
  pageSize.value = newSize;
  currentPage.value = 1; // Voltar para primeira página
  fetchStudies();
}

function getVisiblePages() {
  const pages = [];
  const maxVisible = 5;
  
  if (totalPages.value <= maxVisible) {
    // Mostrar todas as páginas se houver 5 ou menos
    for (let i = 1; i <= totalPages.value; i++) {
      pages.push(i);
    }
  } else {
    // Mostrar páginas ao redor da página atual
    let start = Math.max(1, currentPage.value - 2);
    let end = Math.min(totalPages.value, start + maxVisible - 1);
    
    // Ajustar se estamos no final
    if (end === totalPages.value) {
      start = Math.max(1, end - maxVisible + 1);
    }
    
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
  }
  
  return pages;
}

function resetFilters() {
  searchTerm.value = '';
  selectedModality.value = '';
  dateFilter.value = '';
  currentPage.value = 1;
  fetchStudies();
}



function editStudy(study) {
  // Verificar se o exame foi juntado
  if (study.status && study.status.includes('Junção')) {
    const targetAccession = study.status.match(/OS (\d+)/)?.[1] || 'desconhecido';
    const message = `Este exame (${study.accession_number}) foi juntado com o accession ${targetAccession}. 
    As imagens e séries foram movidas para o accession de destino. 
    Status: ${study.status}`;
    showBanner('warning', message);
    return;
  }
  
  selectedStudy.value = study;
  newAccession.value = '';
  editReason.value = '';
  mergeTargetAccession.value = '';
  mergeReason.value = '';
  studyDetails.value = null;
  showEditModal.value = true;
  fetchStudyDetails(study.accession_number);
}

async function fetchStudyDetails(accession) {
  try {
    const res = await apiClient.get(`${API_ENDPOINTS.EDIT_EXAM.BASE}/studies/${accession}`);
    studyDetails.value = res.data;
  } catch (error) {
    showBanner('error', 'Erro ao carregar detalhes do exame');
  }
}

function closeEditModal() {
  showEditModal.value = false;
  selectedStudy.value = null;
  studyDetails.value = null;
  activeTab.value = 'edit';
  showAllImages.value = false;
}

async function updateAccession() {
  if (!newAccession.value) {
    showBanner('error', 'Informe o novo accession!');
    return;
  }
  
  try {
    await apiClient.put(`${API_ENDPOINTS.EDIT_EXAM.BASE}/studies/${selectedStudy.value.accession_number}/edit-accession`, {
      new_accession: newAccession.value,
      reason: editReason.value
    });
    closeEditModal();
    refreshData();
    showBanner('success', 'Accession atualizado com sucesso!');
  } catch (error) {
    showBanner('error', 'Erro ao atualizar accession');
  }
}

async function mergeExam() {
  if (!mergeTargetAccession.value) {
    showBanner('error', 'Informe o accession de destino!');
    return;
  }
  
  try {
    const response = await apiClient.post(`${API_ENDPOINTS.EDIT_EXAM.BASE}/studies/${selectedStudy.value.accession_number}/merge`, {
      target_accession: mergeTargetAccession.value,
      reason: mergeReason.value
    });
    
    closeEditModal();
    refreshData();
    
    // Exibir mensagem detalhada da junção
    const details = response.data.details;
    const message = `Exames juntados com sucesso! 
      Accession ${details.source_accession} foi juntado com ${details.target_accession}. 
      Movidas ${details.moved_series} séries e ${details.moved_images} imagens. 
      O accession de destino agora possui ${details.target_total_series} séries e ${details.target_total_images} imagens.
      O exame origem permaneceu no sistema com status atualizado.`;
    
    showBanner('success', message);
  } catch (error) {
    showBanner('error', 'Erro ao juntar exames');
  }
}

async function deleteSeries(series) {
  if (!confirm('Tem certeza que deseja excluir esta série?')) return;
  
  try {
    await apiClient.delete(`${API_ENDPOINTS.EDIT_EXAM.BASE}/series/${series.series_instance_uid}/delete`, {
      data: { reason: 'Exclusão manual via painel' }
    });
    if (selectedStudy.value) {
      fetchStudyDetails(selectedStudy.value.accession_number);
    }
    refreshData();
    showBanner('success', 'Série excluída com sucesso!');
  } catch (error) {
    showBanner('error', 'Erro ao excluir série');
  }
}

async function deleteImage(image) {
  if (!confirm('Tem certeza que deseja excluir esta imagem?')) return;
  
  try {
    await apiClient.delete(`${API_ENDPOINTS.EDIT_EXAM.BASE}/images/${image.sop_instance_uid}/delete`, {
      data: { reason: 'Exclusão manual via painel' }
    });
    if (selectedStudy.value) {
      fetchStudyDetails(selectedStudy.value.accession_number);
    }
    refreshData();
    showBanner('success', 'Imagem excluída com sucesso!');
  } catch (error) {
    showBanner('error', 'Erro ao excluir imagem');
  }
}

function deleteStudy(study) {
  if (study.status && study.status.includes('Junção')) {
    showBanner('error', 'Não é possível excluir um exame que foi juntado com outro');
    return;
  }
  studyToDelete.value = study;
  deleteConfirmAccession.value = "";
  showDeleteModal.value = true;
}

async function confirmDelete() {
  if (!studyToDelete.value) return;
  try {
    const response = await apiClient.delete(`${API_ENDPOINTS.EDIT_EXAM.BASE}/studies/${studyToDelete.value.accession_number}/delete`, {
      data: { reason: 'Exclusão completa via painel administrativo' }
    });
    refreshData();
    const details = response.data;
    const message = `Exame ${studyToDelete.value.accession_number} excluído com sucesso! \n${details.deleted_files_count} arquivos deletados do Storage.`;
    showBanner('success', message);
  } catch (error) {
    const errorMessage = error.response?.data?.detail || 'Erro ao excluir exame';
    showBanner('error', errorMessage);
  } finally {
    showDeleteModal.value = false;
    studyToDelete.value = null;
  }
}

function cancelDelete() {
  showDeleteModal.value = false;
  studyToDelete.value = null;
}

async function refreshData() {
  await fetchStudies();
}

function formatDate(date) {
  if (!date) return '';
  // Evita problema de timezone ao criar Date com string YYYY-MM-DD
  const [year, month, day] = date.split('-');
  return new Date(year, month - 1, day).toLocaleDateString('pt-BR');
}



function getInitials(name) {
  if (!name) return '';
  return name
    .split(' ')
    .map(w => w[0]?.toUpperCase() || '')
    .join('')
    .substring(0, 2);
}

function getStudyColor(accession) {
  const colors = [
    '#10b981', '#3b82f6', '#8b5cf6',
    '#ec4899', '#f59e0b', '#06b6d4',
    '#84cc16'
  ];
  const num = parseInt(String(accession).replace(/\D/g, ''), 10) || 0;
  return colors[num % colors.length];
}

function getFileName(filePath) {
  if (!filePath) return '';
  return filePath.split('/').pop() || filePath.split('\\').pop() || filePath;
}

function getSeriesImages(series) {
  if (!studyDetails.value || !studyDetails.value.images) return [];
  // Filtrar imagens que pertencem a esta série usando a relação direta
  return studyDetails.value.images.filter(image => 
    image.series_instance_uid === series.series_instance_uid
  );
}

function showBanner(type, message) {
  banner.value = { show: true, type, message };
}

// Watchers
watch([searchTerm, selectedModality, dateFilter], () => {
  currentPage.value = 1; // Reset para primeira página ao filtrar
  fetchStudies();
});

// Lifecycle
onMounted(() => {
  fetchStudies();
  fetchModalities();
});
</script>

<style>
.clinic-btn {
  background-color: var(--clinic-btn-bg) !important;
  color: #fff !important;
  transition: background 0.2s;
}
.clinic-btn:hover {
  background-color: var(--clinic-btn-bg-hover) !important;
}
</style>
